import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { AuthService } from '../../../authentication/application/services/auth.service';
import { NotificationPanelComponent } from '../../../notifications/application/components/notification-panel/notification-panel.component';
import { I18nService } from '../../services/i18n.service';
import { LayoutService } from '../../services/layout.service';
import { SearchService } from '../../services/search.service';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslocoPipe, NotificationPanelComponent],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnDestroy {
  readonly layout = inject(LayoutService);
  readonly currentUser = inject(AuthService).currentUser;
  readonly themeService = inject(ThemeService);

  private readonly authService = inject(AuthService);
  private readonly searchService = inject(SearchService);
  private readonly i18nService = inject(I18nService);
  private readonly router = inject(Router);

  private readonly _searchInput$ = new Subject<string>();
  private readonly _destroy$ = new Subject<void>();

  readonly languages = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' },
  ];
  activeLanguage = this.i18nService.getActiveLanguage();

  constructor() {
    this._searchInput$
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((term) => this.searchService.setQuery(term));
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onLanguageChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.activeLanguage = this.i18nService.setLanguage(select.value);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this._searchInput$.next(input.value);
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.layout.toggleDropdown();
  }

  onLogout(): void {
    this.authService.logout().pipe(takeUntil(this._destroy$)).subscribe({
      complete: () => this.router.navigate(['/auth/login']),
      error: () => this.router.navigate(['/auth/login']),
    });
  }

  @HostListener('document:click')
  closeDropdown(): void {
    this.layout.closeDropdown();
  }
}
